import React from 'react';
import { SelectBaseProps } from './types';
import { Dimensions, Pressable, StyleProp, View, ViewStyle } from 'react-native';
import SelectBase from './SelectBaseInput';
import { getSpacing } from '../../../../utils/spacing';
import Stack from '../../Stack';
import { MaybeRenderNestedScrollViewPortal, useAssertHasNestedScrollViewPortal } from '../../NestedScrollViewPortal';
import { shadows, zIndex } from '../../../../utils/theme';
import { useElementPosition } from '../../../../hooks/useElementPosition';
import { FixedContentContainer } from '../../FixedPositionPortalContainer';
import { PRScrollView } from '../../../shared/Scroll';

const WebSelect: React.FC<SelectBaseProps> = (props) => {
    useAssertHasNestedScrollViewPortal();
    const screenHeight = Dimensions.get('window').height;
    const maxHeight = 0.4 * screenHeight;
    const { ref, position, onLayout } = useElementPosition({});

    return (
        <SelectBase
            {...props}
            onLayout={onLayout}
            inputRef={ref}
            renderOptions={(args) => {
                if (!args.hasFocus || !position) return null;

                // Prefer opening below; only flip above when there is genuinely more room
                // up there AND below can't fit a useful list. (The old screen-half rule
                // flipped fields just past the midline upward despite ample space below.)
                const spaceBelow = position.getMaxVisibleHeightFromBottom();
                const spaceAbove = position.getMaxVisibleHeightFromTop();
                const isPopoverBelow = spaceBelow >= Math.min(maxHeight, 260) || spaceBelow >= spaceAbove;

                const positionSpecificLayoutStyles = ((): StyleProp<ViewStyle> => {
                    if (isPopoverBelow) {
                        return {
                            maxHeight: position.getMaxVisibleHeightFromBottom(),
                            top: position.topAlignFromBottom,
                        };
                    } else {
                        return {
                            bottom: position.bottomAlignFromTop,
                            maxHeight: position.getMaxVisibleHeightFromTop(),
                        };
                    }
                })();

                return (
                    <FixedContentContainer>
                        <MaybeRenderNestedScrollViewPortal>
                            <Pressable
                                onPress={() => {
                                    args.onCloseSelect();
                                }}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    overflow: 'hidden',
                                }}
                            >
                                <View
                                    collapsable={false}
                                    testID="select-options-container"
                                    style={{
                                        position: 'absolute',
                                        left: position.left,
                                        width: position.width,
                                        zIndex: zIndex.prompt,
                                        borderBottomRightRadius: 5,
                                        borderBottomLeftRadius: 5,
                                        ...(positionSpecificLayoutStyles as any),
                                        ...shadows.card,
                                    }}
                                >
                                    {args.hasFocus && (
                                        <PRScrollView
                                            style={{
                                                padding: getSpacing(8),
                                                maxHeight: maxHeight,
                                                backgroundColor: 'white',
                                                borderRadius: getSpacing(1),
                                            }}
                                        >
                                            <Stack vertical fullWidth>
                                                {props.renderDesktopOptions(args)}
                                            </Stack>
                                        </PRScrollView>
                                    )}
                                </View>
                            </Pressable>
                        </MaybeRenderNestedScrollViewPortal>
                    </FixedContentContainer>
                );
            }}
        />
    );
};

export default React.memo(WebSelect);
